/**
 * Who is signed in, for the whole SPA.
 *
 * A context rather than a router loader, and the reason is the sign-in flow
 * itself: signing in and out changes this value in place, without a navigation
 * and without a reload. A loader would have to be revalidated by hand at every
 * point that changes it, and the point it would get missed is the verify screen
 * — the one screen whose entire job is to change it.
 *
 * ── Three states, not two ───────────────────────────────────────────────────
 *
 * `undefined` (still asking), signed out, and signed in. Collapsing the first
 * into the second is the bug that makes a portal flash its sign-in form at a
 * customer who is signed in, on every single page load. `RequireSession` below
 * therefore renders nothing at all until the answer arrives, and the shell
 * renders a skeleton rather than a "Sign in" button.
 */

import { portalApi } from "@/lib/api";
import type { SessionState } from "@/lib/api";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate, useLocation } from "react-router";

export type SessionValue = {
  /** Undefined until `/api/session` answers for the first time. */
  state: SessionState | undefined;
  /** True while the first read is in flight. */
  isLoading: boolean;
  /** The read itself failed — the network, or the Worker being down. Distinct
   *  from "signed out", which is a successful answer. */
  error: Error | null;
  /** Replace the state after a sign-in or sign-out, with no round trip. Stable
   *  for the life of the provider, so it is safe in a dependency list. */
  setState: (next: SessionState) => void;
  /** Re-read from the server. For the account screen after a change. */
  refresh: () => void;
};

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState | undefined>(undefined);
  const [error, setError] = useState<Error | null>(null);
  const [nonce, setNonce] = useState(0);

  // Counts the writes that came from a sign-in or sign-out rather than from a
  // read. A `/api/session` read is stale the moment one of those lands, because
  // the read was sent before the cookie the write arrived with — and the verify
  // screen guarantees exactly that overlap, since both requests leave on the
  // same mount. Committing the read anyway signs the customer back out a beat
  // after signing them in, and the redirect they were promised lands on the
  // sign-in form.
  const writes = useRef(0);

  // `nonce` is not read by the effect — it IS the dependency. Bumping it is how
  // `refresh()` asks for a re-read, the same idiom `useApiQuery` uses. Removing
  // it, as the rule suggests, would make refresh() a no-op. (The suppression has
  // to be the line immediately above the hook, so the reasoning goes here.)
  // biome-ignore lint/correctness/useExhaustiveDependencies: nonce is the refresh trigger
  useEffect(() => {
    const ctrl = new AbortController();
    const before = writes.current;
    let cancelled = false;
    const stale = () => cancelled || writes.current !== before;

    portalApi
      .session(ctrl.signal)
      .then((next) => {
        if (stale()) return;
        setState(next);
        setError(null);
      })
      .catch((e: unknown) => {
        if (stale() || ctrl.signal.aborted) return;
        // A failed read is NOT "signed out". Treating it as one would sign a
        // customer out of a working session because their train went into a
        // tunnel, and they would have to go back to their inbox for a new link.
        setError(e instanceof Error ? e : new Error(String(e)));
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [nonce]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  // Stable across the life of the provider, and it has to be. Consumers put it
  // in effect dependencies; rebuilding it whenever the session changed tore
  // those effects down at the exact moment the session changed, which is the
  // moment they were doing their work.
  const replace = useCallback((next: SessionState) => {
    writes.current += 1;
    setState(next);
    setError(null);
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      state,
      isLoading: state === undefined && error === null,
      error,
      setState: replace,
      refresh,
    }),
    [state, error, replace, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be called inside <SessionProvider />");
  return ctx;
}

/** Narrowed to the signed-in shape, for routes behind `RequireSession`. */
export function useSignedIn(): Extract<SessionState, { signed_in: true }> {
  const { state } = useSession();
  if (!state?.signed_in) {
    throw new Error("useSignedIn called outside <RequireSession />");
  }
  return state;
}

/**
 * Gate for every screen that needs an account.
 *
 * Renders `null` — not a spinner — while the first read is in flight. This is
 * one round trip to our own origin on a warm connection; a spinner that appears
 * and vanishes inside 80ms is a flicker, and the shell above it is already
 * showing the page's furniture.
 *
 * The redirect carries where you were headed, so a link pasted from a support
 * email into a signed-out browser survives the sign-in it triggers.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const { state, error } = useSession();
  const location = useLocation();

  // The read failed. Falling through to the redirect would send a signed-in
  // customer to the sign-in form on a network blip; `children` render and hit
  // their own 401s, which say something true.
  if (error) return <>{children}</>;
  if (state === undefined) return null;

  if (!state.signed_in) {
    const next = `${location.pathname}${location.search}`;
    const to = next === "/" ? "/signin" : `/signin?next=${encodeURIComponent(next)}`;
    return <Navigate to={to} replace />;
  }

  return <>{children}</>;
}
